import React, { useMemo, useState } from 'react';

import {
  discardRatePlanDraft,
  modelRatePlan,
  publishRatePlan,
  rateplanStripeReadiness,
  runRatePlanModel,
  type MigrationStrategy,
  type RatePlanDetail,
  type RatePlanModelRow,
  type StripeReadinessRow,
} from './api';
import RatePlanEditor from './RatePlanEditor';
import { money, pct, when } from './money';

/**
 * Changing a price, as three steps in the order they have to happen.
 *
 * The rule the database enforces is a sequence -- edit a draft, model it, then
 * publish, and publishing refuses unless the model matches the draft as it
 * stands right now. The old screen had all three on one page as peer panels in
 * the wrong order, so the sequence was invisible and the refusals arrived as a
 * surprise at the end. Here the sequence IS the interface: one step at a time,
 * the next step unavailable with its reason stated rather than a dead button.
 *
 * Nothing on step 1 or 2 changes what anybody pays. Only step 3 does, and it
 * says so.
 */

type Step = 1 | 2 | 3;

const STRATEGIES: { value: MigrationStrategy; title: string; detail: string }[] = [
  {
    value: 'never',
    title: 'Nobody moves',
    detail: 'Existing customers keep paying exactly what they pay now, indefinitely. Only new sign-ups get the new prices.',
  },
  {
    value: 'immediate',
    title: 'Move everyone now',
    detail: 'Every existing customer starts paying the new prices straight away.',
  },
  {
    value: 'on_renewal',
    title: 'Move each customer at their next renewal',
    detail: 'Customers stay on their current prices until their billing cycle turns over, then move.',
  },
  {
    value: 'after_notice',
    title: 'Move everyone after a notice period',
    detail: 'Nothing changes for existing customers until the notice period has passed. Use this where the contract requires warning.',
  },
];

export default function RatePlanChange({
  plan,
  liveVersion,
  onChanged,
  onDone,
  onLeave,
  toast,
}: {
  plan: RatePlanDetail;
  /** The version number of the list that is live today, for orientation. */
  liveVersion: number | null;
  onChanged: () => Promise<void> | void;
  onDone: () => void;
  onLeave: () => void;
  toast: (message: string) => void;
}) {
  const cur = plan.currency;
  const [step, setStep] = useState<Step>(1);

  const run = plan.model_run;
  const summary = run?.summary ?? null;
  // A model run recorded before discounts were counted has none of these in its
  // stored summary, so they are read defensively rather than assumed.
  const discounted = summary?.discounted_workspaces ?? 0;
  const givenAway = summary?.discount_given_minor ?? 0;
  const endingSoon = summary?.discounts_ending_90d ?? 0;
  /** The draft has been checked in exactly the form it stands in now. */
  const checked = run != null;

  // --- Step 2: the check -----------------------------------------------------

  const [threshold, setThreshold] = useState('25');
  const [rows, setRows] = useState<RatePlanModelRow[] | null>(null);
  const [busy, setBusy] = useState(false);

  const runCheck = async () => {
    const value = Number(threshold);
    if (!Number.isFinite(value) || value < 0) return toast('Enter a percentage, for example 25');
    setBusy(true);
    try {
      await runRatePlanModel(plan.id, value);
      setRows(await modelRatePlan(plan.id));
      await onChanged();
      toast('Checked');
    } catch (e) {
      toast(`Could not check: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  // --- Step 3: making it live ------------------------------------------------

  const [strategy, setStrategy] = useState<MigrationStrategy>('never');
  const [noticeDays, setNoticeDays] = useState('30');
  const [reason, setReason] = useState('');
  const [requireStripe, setRequireStripe] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [stripe, setStripe] = useState<StripeReadinessRow[] | null>(null);

  const movesPeople = strategy !== 'never';
  const overThreshold = summary?.over_threshold ?? 0;

  /** The server's refusals, said before the click rather than after it. */
  const blockers = useMemo(() => {
    const list: string[] = [];
    if (plan.status !== 'draft') list.push('This version is already live, so there is nothing to publish.');
    if (!plan.bands.length) list.push('There are no asset prices, so this could not bill anybody.');
    if (!plan.editions.length) list.push('There are no plans, so nobody could subscribe.');
    if (!checked) list.push('The change has not been checked in the form it currently stands in — go back to step 2.');
    if (movesPeople && overThreshold > 0 && !reason.trim()) {
      list.push(
        `${overThreshold} customer${overThreshold === 1 ? '' : 's'} would pay more than `
        + `${run?.threshold_pct ?? 25}% extra and this choice moves them, so a written reason is required.`,
      );
    }
    if (strategy === 'after_notice' && !Number.isInteger(Number(noticeDays))) {
      list.push('The notice period needs to be a whole number of days.');
    }
    return list;
  }, [plan, checked, movesPeople, overThreshold, reason, strategy, noticeDays, run]);

  const publish = async () => {
    setPublishing(true);
    try {
      const result = await publishRatePlan(plan.id, {
        strategy,
        noticeDays: strategy === 'after_notice' ? Number(noticeDays) : null,
        appliesTo: {},
        overrideReason: reason.trim() || null,
        thresholdPct: run?.threshold_pct ?? 25,
        requireStripe,
        stripeMode: 'test',
      });
      const moved = Number(result.workspaces_moved ?? 0);
      toast(`These prices are now live${moved ? ` · ${moved} customer(s) moved onto them` : ''}`);
      onDone();
    } catch (e) {
      toast(`Refused: ${(e as Error).message}`);
    } finally {
      setPublishing(false);
    }
  };

  const abandon = async () => {
    if (!window.confirm(
      'Throw away this price change?\n\nNothing that customers pay is affected either way — '
      + 'this only discards the edits you have made.',
    )) return;
    try {
      await discardRatePlanDraft(plan.id);
      toast('Price change discarded');
      onDone();
    } catch (e) {
      toast(`Could not discard: ${(e as Error).message}`);
    }
  };

  const goto = async (next: Step) => {
    setStep(next);
    if (next === 3 && stripe == null) {
      try {
        setStripe(await rateplanStripeReadiness(plan.id, 'test'));
      } catch {
        setStripe([]);
      }
    }
  };

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <p className="lede">
            Changing the <b>{cur}</b> prices
            {liveVersion != null
              ? <span className="muted"> · today&rsquo;s live list is version {liveVersion}</span>
              : null}
          </p>
          <p className="sub" style={{ margin: '4px 0 0' }}>
            Nothing a customer pays changes until you finish step 3. Edit as much as you like first.
          </p>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn" onClick={onLeave}>Back to prices</button>
          <button className="btn" onClick={() => void abandon()}>Throw it away</button>
        </div>
      </div>

      <div className="steps">
        <button
          className={`step ${step === 1 ? 'now' : ''} ${checked ? 'done' : ''}`}
          onClick={() => void goto(1)}
        >
          <span className="n">{checked ? '✓' : '1'}</span>
          <span className="t">Change the prices</span>
        </button>
        <button
          className={`step ${step === 2 ? 'now' : ''} ${checked ? 'done' : ''}`}
          onClick={() => void goto(2)}
        >
          <span className="n">{checked ? '✓' : '2'}</span>
          <span className="t">See who pays more</span>
        </button>
        <button
          className={`step ${step === 3 ? 'now' : ''}`}
          disabled={!checked}
          onClick={() => void goto(3)}
        >
          <span className="n">3</span>
          <span className="t">Make it live</span>
        </button>
      </div>

      {/* ---- Step 1 --------------------------------------------------------- */}
      {step === 1 ? (
        <>
          <RatePlanEditor plan={plan} onChanged={onChanged} toast={toast} />
          <div className="row" style={{ marginTop: 18, gap: 12 }}>
            <button className="btn primary" onClick={() => void goto(2)}>
              Next: see who pays more
            </button>
            <span className="muted" style={{ fontSize: 12.5 }}>
              Every edit saves as you go. None of it is live.
            </span>
          </div>
        </>
      ) : null}

      {/* ---- Step 2 --------------------------------------------------------- */}
      {step === 2 ? (
        <>
          <div className="card">
            <h2 style={{ marginTop: 0 }}>See who pays more</h2>
            <p className="sub">
              Every customer billed in {cur} is priced twice — on the list they are on now, and on the
              one you have just edited — using the assets they actually have today. Customers billed
              in another currency are untouched by this change and are not counted.
            </p>

            <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
              <button className="btn primary" disabled={busy} onClick={() => void runCheck()}>
                {busy ? 'Working it out…' : checked ? 'Check again' : 'Work out the impact'}
              </button>
              <span className="muted" style={{ fontSize: 12.5 }}>Count a rise as steep above</span>
              <input
                className="num"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                style={{ width: 58, textAlign: 'right' }}
              />
              <span className="muted" style={{ fontSize: 12.5 }}>percent</span>
            </div>

            {summary ? (
              <>
                <p className="lede" style={{ margin: '18px 0 14px' }}>
                  {summary.delta_minor === 0 ? (
                    <>Making this live changes nobody&rsquo;s bill.</>
                  ) : summary.delta_minor > 0 ? (
                    <>
                      Making this live would bring in <b>{money(summary.delta_minor, cur)}</b> more a
                      month ({pct(summary.delta_pct)}) across{' '}
                      <b>{summary.workspaces_modelled}</b> customer
                      {summary.workspaces_modelled === 1 ? '' : 's'}.
                      {overThreshold > 0 ? (
                        <>
                          {' '}
                          <b>{overThreshold}</b> of them would pay more than{' '}
                          {run?.threshold_pct ?? threshold}% extra.
                        </>
                      ) : null}
                    </>
                  ) : (
                    <>
                      Making this live would give up <b>{money(Math.abs(summary.delta_minor), cur)}</b> a
                      month ({pct(summary.delta_pct)}) across <b>{summary.workspaces_modelled}</b>{' '}
                      customer{summary.workspaces_modelled === 1 ? '' : 's'}.
                    </>
                  )}
                </p>

                <div className="stats">
                  <Stat k="They pay now" v={money(summary.current_mrr_minor, cur)} />
                  <Stat k="They would pay" v={money(summary.proposed_mrr_minor, cur)} />
                  <Stat
                    k={`Rise over ${run?.threshold_pct ?? threshold}%`}
                    v={String(summary.over_threshold)}
                    tone={summary.over_threshold > 0 ? 'warn' : undefined}
                  />
                  <Stat
                    k="Start paying for the first time"
                    v={String(summary.newly_charged)}
                    tone={summary.newly_charged > 0 ? 'warn' : undefined}
                  />
                  {/* Only when there are any: two permanently-zero tiles teach
                      an admin to stop reading the row. */}
                  {discounted > 0 ? (
                    <Stat k="Given away in discounts" v={money(givenAway, cur)} />
                  ) : null}
                  {endingSoon > 0 ? (
                    <Stat
                      k="Discounts ending in 90 days"
                      v={String(endingSoon)}
                      tone="warn"
                    />
                  ) : null}
                </div>

                {discounted > 0 ? (
                  <p className="hint" style={{ marginTop: 10 }}>
                    {discounted} of these {summary.workspaces_modelled} are on a promo discount, so
                    the figures above are what is actually collected rather than what the price list
                    says.
                    {endingSoon > 0 ? (
                      <>
                        {' '}
                        <b>{endingSoon}</b> of those discounts{endingSoon === 1 ? ' ends' : ' end'}{' '}
                        within ninety days — {endingSoon === 1 ? 'that bill rises' : 'those bills rise'}{' '}
                        on {endingSoon === 1 ? 'its' : 'their'} own, without this or any other price change.
                      </>
                    ) : null}
                  </p>
                ) : null}

                <div className="row" style={{ gap: 12, marginTop: 14, flexWrap: 'wrap' }}>
                  <button
                    className="quiet"
                    onClick={async () => {
                      if (rows) return setRows(null);
                      try {
                        setRows(await modelRatePlan(plan.id));
                      } catch (e) {
                        toast(`Could not read it: ${(e as Error).message}`);
                      }
                    }}
                  >
                    {rows ? 'Hide the customer-by-customer breakdown' : 'Show it customer by customer'}
                  </button>
                  {summary.workspaces_unpriceable > 0 ? (
                    <span className="muted" style={{ fontSize: 12.5 }}>
                      {summary.workspaces_unpriceable} could not be priced (contact-sales plans, and
                      yearly customers this list has no yearly price for).
                    </span>
                  ) : null}
                </div>

                <p className="hint" style={{ marginTop: 10 }}>
                  Worked out {when(run?.run_at)} against the prices exactly as they stand now. Change
                  anything in step 1 and this stops counting — you will be asked to check again.
                </p>
              </>
            ) : (
              <div className="note-bar" style={{ marginTop: 16 }}>
                <div>
                  <b>Not checked yet.</b>
                  These prices cannot be made live until you have seen what they would do to the
                  customers already paying.
                </div>
              </div>
            )}

            {rows && rows.length ? (
              <div className="matrix-scroll" style={{ marginTop: 16, maxHeight: 360, overflow: 'auto' }}>
                <table className="matrix">
                  <thead>
                    <tr>
                      <th>Customer</th><th>Plan</th><th>Billed</th>
                      <th style={{ textAlign: 'right' }}>Assets</th>
                      <th style={{ textAlign: 'right' }}>Pays now</th>
                      <th style={{ textAlign: 'right' }}>Would pay</th>
                      <th style={{ textAlign: 'right' }}>Difference</th>
                      <th>Discount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.account_id}>
                        <td>{r.workspace_name ?? r.account_id.slice(0, 8)}</td>
                        <td>{r.edition_key ?? '—'}</td>
                        <td>{r.billing_term}</td>
                        <td className="num" style={{ textAlign: 'right' }}>{r.billable_assets}</td>
                        <td className="num" style={{ textAlign: 'right' }}>
                          {money(r.current_minor, cur)}
                          {r.promo_code && r.current_list_minor != null ? (
                            <div className="muted" style={{ fontSize: 11 }}>
                              {money(r.current_list_minor, cur)} before
                            </div>
                          ) : null}
                        </td>
                        <td className="num" style={{ textAlign: 'right' }}>
                          {money(r.proposed_minor, cur)}
                          {r.promo_code && r.proposed_list_minor != null ? (
                            <div className="muted" style={{ fontSize: 11 }}>
                              {money(r.proposed_list_minor, cur)} before
                            </div>
                          ) : null}
                        </td>
                        <td className="num" style={{ textAlign: 'right' }}>
                          {r.priceable ? (
                            <>
                              {money(r.delta_minor, cur)}{' '}
                              <span className="muted">{r.delta_pct == null ? '' : pct(r.delta_pct)}</span>
                            </>
                          ) : (
                            <span className="muted">cannot price</span>
                          )}
                        </td>
                        <td>
                          {r.promo_code
                            ? <span className="feat-key">{r.promo_code}</span>
                            : <span className="muted">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>

          <div className="row" style={{ marginTop: 18, gap: 12, flexWrap: 'wrap' }}>
            <button className="btn" onClick={() => void goto(1)}>Back to the prices</button>
            <button className="btn primary" disabled={!checked} onClick={() => void goto(3)}>
              Next: make it live
            </button>
            {!checked ? (
              <span className="muted" style={{ fontSize: 12.5 }}>
                Work out the impact first — that is the step publishing insists on.
              </span>
            ) : null}
          </div>
        </>
      ) : null}

      {/* ---- Step 3 --------------------------------------------------------- */}
      {step === 3 ? (
        <>
          <div className="card">
            <h2 style={{ marginTop: 0 }}>What happens to the customers you already have</h2>
            <p className="sub">
              Making this live always gives new sign-ups the new prices. This is the separate
              decision about everybody already paying — including deciding that they never move,
              which is recorded just as deliberately as moving them.
            </p>

            <div className="opts">
              {STRATEGIES.map((s) => (
                <label key={s.value} className={`opt ${strategy === s.value ? 'on' : ''}`}>
                  <input
                    type="radio"
                    name="strategy"
                    checked={strategy === s.value}
                    onChange={() => setStrategy(s.value)}
                  />
                  <span>
                    <span className="ot">{s.title}</span>
                    <span className="od">{s.detail}</span>
                    {s.value === 'after_notice' && strategy === 'after_notice' ? (
                      <span className="row" style={{ gap: 8, marginTop: 8 }}>
                        <input
                          className="num"
                          value={noticeDays}
                          onChange={(e) => setNoticeDays(e.target.value)}
                          style={{ width: 62, textAlign: 'right' }}
                        />
                        <span className="muted" style={{ fontSize: 12.5 }}>days notice</span>
                      </span>
                    ) : null}
                  </span>
                </label>
              ))}
            </div>

            {movesPeople && overThreshold > 0 ? (
              <>
                <label>
                  Why this is going ahead — {overThreshold} customer
                  {overThreshold === 1 ? '' : 's'} would pay well over the odds
                </label>
                <input
                  value={reason}
                  placeholder="Kept on the record against your name"
                  onChange={(e) => setReason(e.target.value)}
                />
              </>
            ) : null}
          </div>

          <div className="card" style={{ marginTop: 16 }}>
            <h2 style={{ marginTop: 0 }}>Stripe</h2>
            <p className="sub" style={{ marginBottom: 12 }}>
              The prices Stripe needs to carry for this list, and whether it does yet. Creating them
              is a separate sync that has not been connected, so these stay outstanding for now.
            </p>
            {stripe == null ? (
              <p className="muted">Checking…</p>
            ) : (
              <>
                {stripe.map((r) => (
                  <div className="line" key={`${r.price_kind}:${r.plan_key ?? ''}`}>
                    <span className="what">
                      {r.price_kind === 'assets' ? 'Per-asset charge'
                        : r.price_kind === 'edition_annual' ? `${r.plan_key} — yearly`
                        : `${r.plan_key} — monthly`}
                      <span className="note">{r.detail}</span>
                    </span>
                    <span className="lead" />
                    <span className="fig" style={{ color: r.present && !r.drifted ? 'var(--on)' : 'var(--muted2)' }}>
                      {r.present && !r.drifted ? 'in place' : r.drifted ? 'out of date' : 'not created'}
                    </span>
                  </div>
                ))}
                <label className="checkline">
                  <input
                    type="checkbox"
                    checked={requireStripe}
                    onChange={() => setRequireStripe((v) => !v)}
                  />
                  Refuse to go live until Stripe carries every one of them
                </label>
              </>
            )}
          </div>

          <div className="card" style={{ marginTop: 16 }}>
            {blockers.length ? (
              <div className="note-bar warn">
                <div>
                  <b>Not ready yet.</b>
                  <ul>{blockers.map((b) => <li key={b}>{b}</li>)}</ul>
                </div>
              </div>
            ) : (
              <div className="note-bar good">
                <div>
                  <b>Ready.</b>
                  This will become the live {cur} price list.{' '}
                  {strategy === 'never'
                    ? 'Nobody currently paying will move onto it.'
                    : `${summary?.workspaces_modelled ?? 0} existing customer(s) are covered by the choice above.`}{' '}
                  The list they are on today stays exactly as it is, permanently, so anybody left on
                  it keeps paying what they pay.
                </div>
              </div>
            )}
            <div className="row" style={{ gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
              <button className="btn" onClick={() => void goto(2)}>Back to the impact</button>
              <button
                className="btn primary"
                disabled={publishing || blockers.length > 0}
                onClick={() => void publish()}
              >
                {publishing ? 'Making it live…' : `Make these the live ${cur} prices`}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
}

function Stat({ k, v, tone }: { k: string; v: string; tone?: 'warn' }) {
  return (
    <div className="stat">
      <div className="k">{k}</div>
      <div className="v" style={tone === 'warn' ? { color: 'var(--warn)' } : undefined}>{v}</div>
    </div>
  );
}
